import React, { useState, useEffect, useRef, useCallback } from 'react';
import useAuth from '../hooks/useAuth';
import api from '../api/api';
import styles from './MatchingPage.module.css';

import {
  Upload,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Lock,
  Unlock,
  X,
  Zap,
  Layers,
  RotateCcw,
  Coins,
  ArrowRight,
  Loader2,
  Mail
} from 'lucide-react';

import MatchingResult from './MatchingResult';
import MatchingHistory from './MatchingHistory';
import MatchingBatch from '../components/MatchingBatch';

const POLLING_TIMEOUT_MS = 10 * 60 * 1000;

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const ALLOWED_EXTENSIONS_STRING = '.pdf,.doc,.docx';

// ── Les grandes étapes réelles du backend ──
const MAIN_STEPS = [
  { key: 'downloading', text: "Téléchargement sécurisé des documents..." },
  { key: 'extracting',  text: "Extraction du texte et traitement OCR..." },
  { key: 'vectorizing', text: "Calcul des embeddings et similarité sémantique..." },
  { key: 'ai_analysis', text: "Analyse IA en cours..." }
];

// ── Les sous-étapes simulées pour faire patienter pendant 'ai_analysis' ──
const AI_STEPS = [
  "Le LLM analyse le contexte global de l'offre...",
  "Extraction des compétences MUST-HAVE et NICE-TO-HAVE...",
  "Évaluation de l'expertise sectorielle du candidat...",
  "Vérification de la stabilité et de la trajectoire...",
  "Analyse des risques (Flight risk, Ramp-up)...",
  "Génération de l'argumentaire commercial...",
  "Préparation des questions d'entretien...",
  "Calcul des scores finaux et synthèse...",
  "Finalisation du rapport..."
];

const MatchingPage = () => {
  const { refreshToken, quota } = useAuth();

  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [isDragging1, setIsDragging1] = useState(false);
  const [isDragging2, setIsDragging2] = useState(false);

  const [status, setStatus] = useState('idle');
  const [taskId, setTaskId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [resultData, setResultData] = useState(null);

  // ── ÉTATS DU CHARGEMENT HYBRIDE (CATCH-UP SYSTEM) ──
  const [backendStep, setBackendStep] = useState('downloading'); // La réalité du serveur
  const [visualStepIndex, setVisualStepIndex] = useState(0);     // Ce qu'on montre à l'écran
  const [aiSubStep, setAiSubStep] = useState(0);                 // L'animation pendant ai_analysis

  const pollingInterval = useRef(null);
  const file1InputRef = useRef(null);
  const file2InputRef = useRef(null);
  const startTimeRef = useRef(null);

  const [refreshHistoryKey, setRefreshHistoryKey] = useState(0);
  const [lockedFile, setLockedFile] = useState(null);
  const [activeTab, setActiveTab] = useState('single');

  const triggerHistoryRefresh = () => setRefreshHistoryKey((prev) => prev + 1);

  const updateStartTime = useCallback((time) => {
    startTimeRef.current = time;
  }, []);

  const stopProcessing = useCallback(
    (stopStatus, message = '') => {
      if (pollingInterval.current) clearInterval(pollingInterval.current);
      localStorage.removeItem('matchingTask');
      setStatus(stopStatus);
      if (message) setErrorMessage(message);
      updateStartTime(null);
      if (stopStatus === 'completed') setRefreshHistoryKey((prev) => prev + 1);
      
      // Réinitialisation des étapes hybrides
      setBackendStep('downloading');
      setVisualStepIndex(0);
      setAiSubStep(0);
    },
    [updateStartTime]
  );

  const checkTaskStatus = useCallback(
    async (currentTaskId, isRecovery = false) => {
      if (!currentTaskId) return;
      try {
        const response = await api.get(`/status/${currentTaskId}`);
        const data = response.data;
        
        if (data.status === 'completed') {
          setResultData(data);
          stopProcessing('completed');
        } else if (data.status === 'error') {
          stopProcessing('error', data.message || 'Une erreur est survenue durant le matching.');
        } else if (data.status === 'processing') {
          // MISE À JOUR DE LA RÉALITÉ DU BACKEND
          if (data.progress_step) {
            setBackendStep(data.progress_step);
          }
        } else {
          const startTime = startTimeRef.current;
          if (startTime && Date.now() - startTime > POLLING_TIMEOUT_MS) {
            stopProcessing('error', 'Le traitement a dépassé 10 minutes et a été arrêté.');
          }
        }
      } catch {
        if (!isRecovery) stopProcessing('error', 'Erreur de connexion lors de la vérification du statut.');
      }
    },
    [stopProcessing]
  );

  const startPolling = useCallback((currentTaskId) => {
    if (pollingInterval.current) clearInterval(pollingInterval.current);

    // Premier check immédiatement
    checkTaskStatus(currentTaskId, false);

    pollingInterval.current = setInterval(() => {
        checkTaskStatus(currentTaskId, false);
    }, 10000);
    }, [checkTaskStatus]);

  // ── EFFET 1 : LE RATTRAPAGE VISUEL (CATCH-UP) ──
  useEffect(() => {
    if (status !== 'processing') return;

    const targetIndex = MAIN_STEPS.findIndex(s => s.key === backendStep);
    
    // Si l'interface visuelle est en retard par rapport au serveur
    if (visualStepIndex < targetIndex) {
      // On avance d'une étape toutes les 1.5 secondes pour que l'utilisateur ait le temps de lire
      const timer = setTimeout(() => {
        setVisualStepIndex(prev => prev + 1);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [status, backendStep, visualStepIndex]);

  // ── EFFET 2 : L'ANIMATION PENDANT L'IA ──
  useEffect(() => {
    if (status !== 'processing') return;

    // Si visuellement on est arrivé à l'étape 'ai_analysis'
    if (MAIN_STEPS[visualStepIndex].key === 'ai_analysis') {
      const timer = setInterval(() => {
        setAiSubStep((prev) => (prev < AI_STEPS.length - 1 ? prev + 1 : prev));
      }, 12000); // Défilement toutes les 12 secondes
      return () => clearInterval(timer);
    }
  }, [status, visualStepIndex]);

  // Restauration de session
  useEffect(() => {
    const savedTaskJSON = localStorage.getItem('matchingTask');
    if (savedTaskJSON) {
      try {
        const savedTask = JSON.parse(savedTaskJSON);
        if (savedTask?.id && savedTask.status === 'processing') {
          setTaskId(savedTask.id);
          setStatus('processing');
          if (savedTask.startTime) updateStartTime(savedTask.startTime);
          if (savedTask.fileName1) setFile1({ name: savedTask.fileName1 });
          if (savedTask.fileName2) setFile2({ name: savedTask.fileName2 });
          checkTaskStatus(savedTask.id, true).then(() => {
            const startTime = startTimeRef.current;
            const isTimeout = startTime && Date.now() - startTime > POLLING_TIMEOUT_MS;
            if (!isTimeout) startPolling(savedTask.id);
          });
        } else {
          localStorage.removeItem('matchingTask');
        }
      } catch {
        localStorage.removeItem('matchingTask');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && status === 'processing' && taskId) {
        checkTaskStatus(taskId, false);
        startPolling(taskId);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      if (pollingInterval.current) clearInterval(pollingInterval.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [status, taskId, checkTaskStatus, startPolling]);

  const validateAndSet = (file, setter, inputRef, label) => {
    if (file && ALLOWED_MIME_TYPES.includes(file.type)) {
      setter(file);
      setErrorMessage('');
    } else {
      setter(null);
      setErrorMessage(`Fichier ${label} invalide. Formats acceptés : PDF, DOC, DOCX.`);
      if (inputRef.current) inputRef.current.value = null;
    }
  };

  const handleFile1Change = (file) => validateAndSet(file, setFile1, file1InputRef, 'CV');
  const handleFile2Change = (file) => validateAndSet(file, setFile2, file2InputRef, 'Fiche de poste');

  const handleDrop = (e, handler, setDragging) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handler(file);
  };

  const removeFile = (e, setter, inputRef, fileKey) => {
    e.stopPropagation();
    if (lockedFile === fileKey) return;
    setter(null);
    if (inputRef.current) inputRef.current.value = null;
  };

  const toggleLock = (e, fileKey) => {
    e.stopPropagation();
    setLockedFile((prev) => (prev === fileKey ? null : fileKey));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file1 || !file2) {
      setErrorMessage('Veuillez sélectionner les deux fichiers.');
      return;
    }
    
    // On réinitialise les états de chargement hybride
    setStatus('processing');
    setBackendStep('downloading');
    setVisualStepIndex(0);
    setAiSubStep(0);
    setErrorMessage('');
    
    const formData = new FormData();
    formData.append('file1', file1);
    formData.append('file2', file2);
    
    try {
      const response = await api.post('/matching/process', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { task_id, new_token } = response.data;
      if (new_token) refreshToken(new_token);
      setTaskId(task_id);
      const startTime = Date.now();
      updateStartTime(startTime);
      localStorage.setItem(
        'matchingTask',
        JSON.stringify({
          id: task_id,
          status: 'processing',
          fileName1: file1.name,
          fileName2: file2.name,
          startTime,
        })
      );
      startPolling(task_id);
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.response?.data?.error || "Erreur lors de l'envoi des fichiers.");
    }
  };

  const resetState = () => {
    if (lockedFile !== 'file1') {
      setFile1(null);
      if (file1InputRef.current) file1InputRef.current.value = null;
    }
    if (lockedFile !== 'file2') {
      setFile2(null);
      if (file2InputRef.current) file2InputRef.current.value = null;
    }
    stopProcessing('idle');
    setTaskId(null);
    setResultData(null);
  };

  const renderDropZone = ({ file, fileKey, inputRef, onFileChange, isDragging, setDragging, label, icon: Icon }) => {
    const isLocked = lockedFile === fileKey;
    const hasFile = Boolean(file);

    return (
      <div
        className={`${styles.dropZone} ${hasFile ? styles.dropZoneActive : ''} ${isDragging ? styles.dropZoneDragging : ''} ${isLocked ? styles.dropZoneLocked : ''}`}
        onClick={() => !isLocked && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (!isLocked) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => !isLocked && handleDrop(e, onFileChange, setDragging)}
        role="button"
        tabIndex={0}
      >
        <input type="file" hidden ref={inputRef} disabled={isLocked} accept={ALLOWED_EXTENSIONS_STRING} onChange={(e) => onFileChange(e.target.files[0])} />

        {hasFile && (
          <button type="button" className={`${styles.lockBtn} ${isLocked ? styles.lockBtnActive : ''}`} onClick={(e) => toggleLock(e, fileKey)}>
            {isLocked ? <Lock size={13} strokeWidth={2.5} /> : <Unlock size={13} strokeWidth={2} />}
          </button>
        )}

        {hasFile && !isLocked && (
          <button type="button" className={styles.removeBtn} onClick={(e) => removeFile(e, fileKey === 'file1' ? setFile1 : setFile2, inputRef, fileKey)}>
            <X size={13} strokeWidth={2.5} />
          </button>
        )}

        {hasFile ? (
          <div className={styles.fileInfo}>
            <div className={styles.fileIconWrap}><FileText size={28} strokeWidth={1.5} /></div>
            <p className={styles.fileLabel}>{label}</p>
            <p className={styles.fileName}>{file.name}</p>
            {isLocked && <span className={styles.lockedBadge}><Lock size={10} strokeWidth={2.5} /> Verrouillé</span>}
          </div>
        ) : (
          <div className={styles.emptyZone}>
            <div className={styles.uploadIconWrap}><Icon size={26} strokeWidth={1.5} /></div>
            <p className={styles.dropLabel}>{label}</p>
            <p className={styles.dropHint}>Glissez ou <span className={styles.browseLink}>parcourez</span></p>
            <p className={styles.dropFormats}>PDF · DOC · DOCX</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderInner}>
          <div className={styles.pageTitleGroup}>
            <div className={styles.pageIconWrap}><Zap size={20} strokeWidth={2} /></div>
            <div>
              <h1 className={styles.pageTitle}>Matching CV / Poste</h1>
              <p className={styles.pageSubtitle}>Évaluez la compatibilité entre un candidat et une offre</p>
            </div>
          </div>
          {quota !== null && quota !== undefined && (
            <div className={styles.quotaBadge}>
              <Coins size={14} strokeWidth={2} />
              <span><strong>{quota}</strong> crédit{quota > 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
        <div className={styles.tabs}>
          <button className={`${styles.tab} ${activeTab === 'single' ? styles.tabActive : ''}`} onClick={() => setActiveTab('single')}><Zap size={15} strokeWidth={2} />Matching unique</button>
          <button className={`${styles.tab} ${activeTab === 'batch' ? styles.tabActive : ''}`} onClick={() => setActiveTab('batch')}><Layers size={15} strokeWidth={2} />Classement de CVs</button>
        </div>
      </div>

      <div className={styles.card}>
        {activeTab === 'single' ? (
          <>
            {/* IDLE */}
            {status === 'idle' && (
              <div className={styles.formSection}>
                <div className={styles.formHeader}>
                  <h2 className={styles.formTitle}>Lancer une analyse</h2>
                  <p className={styles.formDesc}>Déposez un CV et une fiche de poste pour obtenir un score de compatibilité détaillé.</p>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className={styles.dropGrid}>
                    {renderDropZone({ file: file1, fileKey: 'file1', inputRef: file1InputRef, onFileChange: handleFile1Change, isDragging: isDragging1, setDragging: setIsDragging1, label: 'CV du candidat', icon: Upload })}
                    <div className={styles.dropGridDivider} aria-hidden="true">
                      <div className={styles.dropGridDividerLine} />
                      <ArrowRight size={18} strokeWidth={1.5} className={styles.dropGridDividerIcon} />
                      <div className={styles.dropGridDividerLine} />
                    </div>
                    {renderDropZone({ file: file2, fileKey: 'file2', inputRef: file2InputRef, onFileChange: handleFile2Change, isDragging: isDragging2, setDragging: setIsDragging2, label: 'Fiche de poste', icon: FileText })}
                  </div>
                  {errorMessage && <div className={styles.alert}><AlertTriangle size={15} strokeWidth={2} />{errorMessage}</div>}
                  <div className={styles.submitRow}>
                    <button type="submit" className={styles.submitBtn} disabled={!file1 || !file2}><Zap size={16} strokeWidth={2} />Lancer le matching</button>
                  </div>
                </form>
              </div>
            )}

            {/* PROCESSING - THE SKELETON PREVIEW */}
            {status === 'processing' && (
              <div className={styles.skeletonSection}>
                
                {/* Header d'activité en temps réel */}
                <div className={styles.skeletonLiveHeader}>
                  <div className={styles.skeletonLiveIcon}>
                    <Loader2 className={styles.spin} size={20} />
                  </div>
                  <div className={styles.skeletonLiveText}>
                    <h4>Analyse en cours</h4>
                    <p className={styles.skeletonStepMessage}>
                      {MAIN_STEPS[visualStepIndex]?.key === 'ai_analysis' 
                        ? AI_STEPS[aiSubStep] 
                        : MAIN_STEPS[visualStepIndex]?.text}
                    </p>
                  </div>
                </div>

                {/* Le Skeleton Grid qui reste statique en dessous */}
                <div className={styles.skeletonGrid}>
                  <div className={styles.skeletonScoresCol}>
                    <div className={`${styles.shimmer} ${styles.skeletonGauge}`} />
                    <div className={`${styles.shimmer} ${styles.skeletonLine}`} style={{ width: '80%', height: '24px', marginTop: '16px' }} />
                  </div>
                  
                  <div className={styles.skeletonSummaryCol}>
                    <div className={`${styles.shimmer} ${styles.skeletonLine}`} style={{ width: '50%', height: '28px', marginBottom: '24px' }} />
                    <div className={`${styles.shimmer} ${styles.skeletonLine}`} style={{ width: '100%', marginBottom: '12px' }} />
                    <div className={`${styles.shimmer} ${styles.skeletonLine}`} style={{ width: '100%', marginBottom: '12px' }} />
                    <div className={`${styles.shimmer} ${styles.skeletonLine}`} style={{ width: '70%', marginBottom: '24px' }} />
                    
                    <div className={styles.skeletonSwot}>
                      <div className={`${styles.shimmer} ${styles.skeletonBox}`} />
                      <div className={`${styles.shimmer} ${styles.skeletonBox}`} />
                    </div>
                  </div>
                </div>
                
                <div className={styles.skeletonPillarsWrapper}>
                   <div className={styles.skeletonPillarsGrid}>
                     {[1, 2, 3, 4].map(i => (
                       <div key={i} className={`${styles.shimmer} ${styles.skeletonPillarCard}`} />
                     ))}
                   </div>
                </div>
              </div>
            )}

            {/* COMPLETED */}
            {status === 'completed' && (
              <div className={styles.statusSection}>
                <div className={styles.successBadge}><CheckCircle2 size={22} strokeWidth={2} />Analyse terminée</div>
                <h3 className={styles.statusTitle}>Résultat disponible</h3>
                <p className={styles.statusDesc}>Le score de compatibilité a été calculé avec succès.</p>
                {resultData && <MatchingResult data={resultData} />}
                <div className={styles.statusActions}>
                  <button onClick={resetState} className={styles.resetBtn}><RotateCcw size={15} strokeWidth={2} />{lockedFile ? 'Nouveau matching' : 'Recommencer'}</button>
                </div>
              </div>
            )}

            {/* ERROR */}
            {status === 'error' && (
              <div className={styles.statusSection} style={{ textAlign: 'center' }}>
                <div className={styles.errorBadge} style={{ margin: '0 auto 15px' }}>
                  <AlertTriangle size={22} strokeWidth={2} /> Erreur
                </div>
                <h3 className={styles.statusTitle}>Oups, le matching a été interrompu</h3>
                <div className={styles.alert} style={{ justifyContent: 'center', marginBottom: '20px' }}>
                  <AlertTriangle size={15} strokeWidth={2} />
                  <span>{errorMessage}</span>
                </div>
                <div className={styles.statusActions} style={{ justifyContent: 'center', gap: '15px' }}>
                  <button onClick={resetState} className={styles.resetBtn}>
                    <RotateCcw size={15} strokeWidth={2} /> Réessayer
                  </button>
                  {/* Utilise <Link to="/support"> si react-router-dom est importé */}
                  <a href="/support" className={styles.resetBtn} style={{ textDecoration: 'none', backgroundColor: 'transparent', border: '1px solid var(--color-border)' }}>
                    <Mail size={15} strokeWidth={2} /> Contacter un admin
                  </a>
                </div>
              </div>
            )}
          </>
        ) : (
          <MatchingBatch onBatchFinish={triggerHistoryRefresh} quota={quota} refreshToken={refreshToken} />
        )}
      </div>

      {/* ── HISTORIQUE ── */}
      <div className={styles.historySection}>
        <div className={styles.historySeparator}>
          <div className={styles.historySeparatorLine} />
          <span className={styles.historySeparatorLabel}>Historique</span>
          <div className={styles.historySeparatorLine} />
        </div>
        <MatchingHistory refreshKey={refreshHistoryKey} />
      </div>
    </div>
  );
};

export default MatchingPage;